import {
  ClientType,
  JobType,
  RelatedAddressType,
  RoutingType,
  ShipmentType
} from '@constants';
import { Prototype } from '@core';
import { useAlertMutationEnhancer, useCUDTransportJob } from '@hooks';
import loadable from '@loadable/component';
import { UIUtils, AgUtils } from '@utils';
import { mappedContainerInvoiceStatus } from '@utils/mapped';
import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams
} from 'ag-grid-community';
import qs from 'query-string';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import trans from 'translation';
import { KLabel } from 'uikit';

const ContainerForm = loadable(() => import('./Container/Form'));

export const renderRouting = (
  container: any,
  types: string | string[],
  isWarehouse = false
) => {
  if (typeof types === 'string') {
    types = [types];
  }

  if (container) {
    const routings = container?.jobContainerRoutings;
    if (routings) {
      const filteredItems = routings
        .filter((i: any) => types.includes(i.routingType))
        .map((i: any) =>
          isWarehouse ? i.relatedPartySite?.relatedSite?.name : i.client?.name
        )
        .filter((i: any) => i);
      return filteredItems.join(' - ');
    }
  }

  return '';
};

export const renderEstTime = (
  container: any,
  types: string | string[],
  parse: boolean = true
) => {
  if (typeof types === 'string') {
    types = [types];
  }

  if (container) {
    const routings = container?.jobContainerRoutings;
    if (routings) {
      const item = routings
        .filter((i: any) => types.includes(i.routingType))
        .map((i: any) => i.deliveryPickupTimeslotEstDatetime)?.[0];
      return parse ? Prototype.date.formatDT(item) ?? '' : item;
    }
  }

  return '';
};

export const useOpenTransportJobDetail = (shipmentType: ShipmentType) => {
  const history = useHistory();
  const onOpen = useCallback(
    (id: any, query?: any) => {
      const _query = {
        ...query,
        shipmentType
      };
      history.push(`/admin/transport-jobs/${id}${`?${qs.stringify(_query)}`}`, {
        canBack: true
      });
    },
    [history, shipmentType]
  );
  const onAdd = useCallback(() => {
    onOpen('new', { shipmentType });
  }, [onOpen, shipmentType]);

  return {
    onAdd,
    onOpen
  };
};

export const useDeleteTransportJob = () => {
  const { deleteMutation } = useCUDTransportJob();

  return useAlertMutationEnhancer({
    mutation: deleteMutation,
    message: trans('message_delete_transport_job')
  });
};

export const onShowTransportJob = (
  id: number | string | 'new',
  query?: any
) => {
  window.open(
    `/admin/transport-jobs/${id}${query ? `?${qs.stringify(query)}` : ''}`,
    '_blank'
  );
};

export const onShowContainer = (
  v?: any,
  mParams?: {
    transportJobId?: number | string;
    selectedContainer?: any;
    setItem?: (v: any) => void;
  },
  name?: string
) => {
  UIUtils.popup.open({
    title: trans('container'),
    maxWidth: 'md',
    content: () => <ContainerForm item={v} {...mParams} name={name} />
  });
};

export const mappedConsignCardData = {
  [JobType.Import]: {
    title: 'consignee',
    typeIn: [ClientType.Consignee]
  },
  [JobType.RailInbound]: {
    title: 'consignee',
    typeIn: [ClientType.Consignee]
  },
  [JobType.Export]: {
    title: 'consignor',
    typeIn: [ClientType.Consignor]
  },
  [JobType.RailOutbound]: {
    title: 'consignor',
    typeIn: [ClientType.Consignor]
  },
  [JobType.Misc]: {
    title: 'consignee_consignor',
    typeIn: [ClientType.Consignee, ClientType.Consignor]
  }
};

export const mappedWarehouseCardData = {
  [JobType.Import]: {
    title: 'delivery_warehouse',
    typeIn: [RelatedAddressType.DeliveryWarehouse]
  },
  [JobType.RailInbound]: {
    title: 'delivery_warehouse',
    typeIn: [RelatedAddressType.DeliveryWarehouse]
  },
  [JobType.Export]: {
    title: 'pickup_warehouse',
    typeIn: [RelatedAddressType.PickupWarehouse]
  },
  [JobType.RailOutbound]: {
    title: 'pickup_warehouse',
    typeIn: [RelatedAddressType.PickupWarehouse]
  },
  [JobType.Misc]: {
    title: 'warehouse',
    typeIn: [
      RelatedAddressType.DeliveryWarehouse,
      RelatedAddressType.PickupWarehouse
    ]
  }
};

// const EditCell = ({ value, data }: ICellRendererParams) => {
//   const { onOpen } = useOpenTransportJobDetail(data?.shipmentType);

//   const onPress = useCallback(() => onOpen(value), [onOpen, value]);

//   return <KButton.Icon size="sm" icon="Edit" onPress={onPress} />;
// };

export const colDefs = (): ColDef<any>[] => {
  return [
    // {
    //   headerName: trans('action'),
    //   field: 'id',
    //   sortable: false,
    //   ...AgUtils.options.centerContent,
    //   ...AgUtils.options.baseXs,
    //   cellRenderer: EditCell
    // },
    {
      headerName: trans('job_number'),
      field: 'jobNumber',
      cellRenderer: ({ value, data }: ICellRendererParams) => {
        return (
          <KLabel.Text isLink to={`/admin/transport-jobs/${data?.id}`}>
            {value}
          </KLabel.Text>
        );
      }
    },
    {
      headerName: trans('job_status'),
      field: 'jobProgress',
      ...AgUtils.options.chip()
    },
    {
      headerName: trans('job_type'),
      field: 'jobType'
    },
    {
      headerName: trans('customer_reference'),
      field: 'referenceNumber',
      ...AgUtils.options.baseXLg
    },
    {
      headerName: trans('vessel'),
      field: 'vessel.vessel'
    },
    {
      headerName: trans('lloyd'),
      field: 'vessel.lloyd'
    },
    {
      headerName: trans('voyage'),
      field: 'voyage'
    },
    {
      headerName: trans('unloco_board_of_loading'),
      field: 'unlocoBoardOfLoading',
      ...AgUtils.options.baseXLg
    },
    {
      headerName: trans('unloco_board_of_discharge'),
      field: 'unlocoBoardOfDischarge',
      ...AgUtils.options.baseXLg
    },
    {
      headerName: trans('eta'),
      field: 'eta',
      type: 'vnDateTime'
    },
    {
      headerName: trans('etd'),
      field: 'etd',
      type: 'vnDateTime'
    },
    {
      headerName: trans('imp_available_date'),
      field: 'avail',
      type: 'vnDate',
      ...AgUtils.options.baseXLg
    },
    {
      headerName: trans('imp_storage_start_date'),
      field: 'stor',
      type: 'vnDate',
      ...AgUtils.options.baseXLg
    },
    {
      headerName: trans('imp_storage_last_free_date'),
      field: 'storLastFreeDate',
      type: 'vnDate',
      ...AgUtils.options.base2XLg
    },
    {
      headerName: trans('1st_free_date'),
      field: 'firstFreeDate',
      type: 'vnDate'
    },
    {
      headerName: trans('discharge_date'),
      field: 'firstDischargeDate',
      type: 'vnDate'
    },
    {
      headerName: trans('exp_receival_commencement_date'),
      field: 'expRecvDate',
      type: 'vnDate',
      ...AgUtils.options.base2XLg
    },
    {
      headerName: trans('cargo_cutoff_date'),
      field: 'cutOffDate',
      type: 'vnDate'
    },
    {
      headerName: trans('reefer_cutoff_date'),
      field: 'reeferCutoffDate',
      type: 'vnDate'
    },
    {
      headerName: trans('empty_receival_date'),
      field: 'emptyReceivalCommencementDate',
      type: 'vnDate'
    },
    {
      headerName: trans('empty_cutoff_date'),
      field: 'emptyCutoffDate',
      type: 'vnDate'
    },
    {
      headerName: trans('hazardous_receival_date'),
      field: 'hazardousReceivalCommencementDate',
      type: 'vnDate',
      ...AgUtils.options.baseXLg
    },
    {
      headerName: trans('hazardous_cutoff_date'),
      field: 'hazardousCutoffDate',
      type: 'vnDate',
      ...AgUtils.options.baseXLg
    },
    {
      headerName: trans('agent_code'),
      field: 'agentClient.code'
    },
    {
      headerName: trans('agent_name'),
      field: 'agentClient.name'
    },
    {
      headerName: trans('consignee_consignor_code'),
      field: 'consignClient.code'
    },
    {
      headerName: trans('consignee_consignor_name'),
      field: 'consignClient.name'
    },
    {
      headerName: trans('account_receivable_code'),
      field: 'relatedPartyArClient.relatedClient.code'
    },
    {
      headerName: trans('account_receivable_name'),
      field: 'relatedPartyArClient.relatedClient.name'
    },
    {
      headerName: trans('warehouse_code'),
      field: 'warehouseClient.code'
    },
    {
      headerName: trans('warehouse_name'),
      field: 'warehouseClient.name'
    },
    {
      headerName: trans('container_number'),
      field: 'jobContainer.containerNumber'
    },
    {
      headerName: trans('container_status'),
      field: 'jobContainer.containerProgress',
      ...AgUtils.options.chip()
    },
    {
      headerName: trans('invoice_status'),
      field: 'jobContainer.containerInvoiceStatus',
      minWidth: 150,
      ...AgUtils.options.chips({
        width: 100,
        uppercase: true,
        mapLabelData: mappedContainerInvoiceStatus()
      })
    },
    {
      headerName: trans('drop_mode'),
      field: 'jobContainer.dropMode.name'
    },
    {
      headerName: trans('container_size'),
      field: 'jobContainer.containerSize.code'
    },
    {
      headerName: trans('cto_rail_head'),
      colId: 'cto',
      valueGetter: ({ data }: ValueGetterParams) => {
        const container = data?.jobContainer;
        return renderRouting(container, [
          RoutingType.Cto,
          RoutingType.RailHead
        ]);
      }
    },
    {
      headerName: trans('cto_rail_head_est_time'),
      colId: 'ctoEstTime',
      valueGetter: ({ data }: ValueGetterParams) => {
        const container = data?.jobContainer;
        return (
          renderEstTime(
            container,
            [RoutingType.Cto, RoutingType.RailHead],
            false
          ) || null
        );
      },
      type: 'vnDateTime'
    },
    {
      headerName: trans('wait_depot_full'),
      colId: 'wdf',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderRouting(data?.jobContainer, [
          RoutingType.WaitDepotFullDropOff,
          RoutingType.WaitDepotFullStopBy
        ]);
      }
    },
    {
      headerName: trans('wait_depot_full_est_time'),
      colId: 'wdfEstTime',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderEstTime(
          data?.jobContainer,
          [RoutingType.WaitDepotFullDropOff, RoutingType.WaitDepotFullStopBy],
          false
        );
      },
      type: 'vnDateTime'
    },
    {
      headerName: trans('delivery_warehouse'),
      colId: 'dw',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderRouting(
          data?.jobContainer,
          RoutingType.DeliveryWarehouse,
          true
        );
      }
    },
    {
      headerName: trans('delivery_warehouse_est_time'),
      colId: 'dwEstTime',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderEstTime(
          data?.jobContainer,
          RoutingType.DeliveryWarehouse,
          false
        );
      },
      type: 'vnDateTime'
    },
    {
      headerName: trans('pickup_warehouse'),
      colId: 'pw',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderRouting(
          data?.jobContainer,
          RoutingType.PickupWarehouse,
          true
        );
      }
    },
    {
      headerName: trans('pickup_warehouse_est_time'),
      colId: 'pwEstTime',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderEstTime(
          data?.jobContainer,
          RoutingType.PickupWarehouse,
          false
        );
      },
      type: 'vnDateTime'
    },
    {
      headerName: trans('wait_depot_empty'),
      colId: 'wde',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderRouting(data?.jobContainer, [
          RoutingType.WaitDepotEmptyDropOff,
          RoutingType.WaitDepotEmptyStopBy
        ]);
      }
    },
    {
      headerName: trans('wait_depot_empty_est_time'),
      colId: 'wdeEstTime',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderEstTime(
          data?.jobContainer,
          [RoutingType.WaitDepotEmptyDropOff, RoutingType.WaitDepotEmptyStopBy],
          false
        );
      },
      type: 'vnDateTime'
    },
    {
      headerName: trans('empty_yard'),
      colId: 'emptyYard',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderRouting(data?.jobContainer, RoutingType.EmptyYard);
      }
    },
    {
      headerName: trans('empty_yard_est_time'),
      colId: 'emptyYardEstTime',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderEstTime(data?.jobContainer, RoutingType.EmptyYard, false);
      },
      type: 'vnDateTime'
    },
    {
      headerName: trans('wait_depot_misc'),
      colId: 'wdm',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderRouting(data?.jobContainer, [
          RoutingType.WaitDepotMiscDropOff,
          RoutingType.WaitDepotMiscStopBy
        ]);
      }
    },
    {
      headerName: trans('warehouse_misc'),
      colId: 'wm',
      valueGetter: ({ data }: ValueGetterParams) => {
        return renderRouting(data?.jobContainer, RoutingType.Warehouse, true);
      }
    }
  ];
};
